/**
 * CW-SF-M5 — shared shell for the 4 SF dashboard widgets.
 *
 * Wraps a widget body with:
 *   - "Connect Salesforce" CTA when connection_state='missing'
 *   - Reconnect banner above content when state='degraded'
 *   - "Last synced N ago" footer when stale=true
 *
 * Cross-References:
 *   - sprint-cw-sf-m5-widgets.md TKT-1
 */
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Plug } from 'lucide-react';
import { formatRelativeMinutes, type SfWidgetEnvelope } from './sfWidgetUtils';

interface Props {
  envelope: SfWidgetEnvelope | null;
  loading: boolean;
  error: Error | null;
  emptyMessage?: string;
  hasItems?: boolean;
  children: ReactNode;
}

export function SfWidgetShell({
  envelope,
  loading,
  error,
  emptyMessage = 'No data yet.',
  hasItems = true,
  children,
}: Props) {
  if (loading && !envelope) {
    return <div className="sf-widget__loading">Loading…</div>;
  }
  if (error) {
    return (
      <div className="sf-widget__error">
        <AlertCircle size={14} /> Failed to load: {error.message}
      </div>
    );
  }
  if (envelope?.connection_state === 'missing') {
    return (
      <div className="sf-widget__cta">
        <Plug size={18} />
        <span>Connect Salesforce to see your data.</span>
        <Link to="/integrations" className="sf-widget__cta-link">
          Connect →
        </Link>
      </div>
    );
  }
  return (
    <>
      {envelope?.connection_state === 'degraded' && (
        <div className="sf-widget__banner sf-widget__banner--reconnect">
          <AlertCircle size={14} />
          Salesforce connection needs attention.{' '}
          <Link to="/integrations">Reconnect →</Link>
        </div>
      )}
      {!hasItems ? (
        <div className="sf-widget__empty">{emptyMessage}</div>
      ) : (
        children
      )}
      {envelope?.stale && envelope.last_synced_at && (
        <div className="sf-widget__footer">
          Last synced {formatRelativeMinutes(envelope.last_synced_at)}
        </div>
      )}
    </>
  );
}
